import { ChevronDown, ChevronRight, Lock, Play, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { Select } from '@/components/ui/Select';
import type { LibraryDocument } from '@/features/documents/api';
import { useDocuments, useProperties } from '@/features/documents/queries';
import { useSession } from '@/lib/auth/useSession';
import { formatDate, formatDateTime } from '@/lib/format';
import { isActive, monthLabel, previousMonth, type Period, type PipelineRun } from './api';
import { useCloseMonth, useProcessMonth, usePeriodRuns, usePeriods, useRun } from './queries';

/**
 * Periods (build plan P2-H1). Process loads a month's AppFolio reports from
 * the library (only new or changed ones; it is always safe to press again).
 * Close processes, then locks the month if every report loaded. A closed
 * month takes another Process or Close only with a note saying why.
 */
export function PeriodsScreen() {
  const { locked } = useSession();
  const properties = useProperties();
  const periods = usePeriods();
  const [propertyKey, setPropertyKey] = useState('');
  const [month, setMonth] = useState(previousMonth());
  const [note, setNote] = useState('');
  const [runId, setRunId] = useState<string>();
  const [confirmClose, setConfirmClose] = useState(false);
  const process = useProcessMonth();
  const close = useCloseMonth();
  const run = useRun(runId);

  const key = propertyKey || properties.data?.[0]?.key || '';
  const period = periods.data?.find((p) => p.propertyKey === key && p.month === month);
  const closed = period?.status === 'closed';
  const busy = process.isPending || close.isPending || (!!run.data && isActive(run.data));
  const needsNote = closed && !note.trim();
  const start = (which: typeof process) =>
    which.mutate(
      { propertyKey: key, month, note },
      {
        onSuccess: (r) => {
          setRunId(r.run.id);
          setNote('');
        },
      },
    );

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-semibold text-ink">Periods</h1>
            <p className="mt-1 text-sm text-ink-soft">
              Process loads a month&rsquo;s AppFolio reports from Documents. Close processes, then locks the month.
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => void periods.refetch()}
            disabled={periods.isFetching}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw size={16} className={periods.isFetching ? 'animate-spin' : ''} />
          </Button>
        </div>

        <div className="mt-6 rounded-xl border border-line bg-surface p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="text-xs font-semibold text-ink-soft">
              Property
              <Select value={key} onChange={(e) => setPropertyKey(e.target.value)}>
                {properties.data?.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </label>
            <label className="text-xs font-semibold text-ink-soft">
              Month
              <input
                type="month"
                value={month}
                onChange={(e) => e.target.value && setMonth(e.target.value)}
                className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-sage"
              />
            </label>
            <div className="flex items-end">
              <PeriodStatusPill period={period} />
            </div>
            <label className="text-xs font-semibold text-ink-soft sm:col-span-3">
              Note {closed ? '(required: this month is closed; say what changed)' : '(optional)'}
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder={closed ? 'e.g. Rent roll re-exported: parking posted late' : ''}
                className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm text-ink outline-none focus:border-sage"
              />
            </label>
          </div>
          <div className="relative mt-3 flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => start(process)}
              disabled={!key || busy || needsNote}
              lockedReason={locked('pipeline.run')}
            >
              <Play size={15} /> Process
            </Button>
            <Button
              onClick={() => setConfirmClose(true)}
              disabled={!key || busy || needsNote}
              lockedReason={locked('period.close')}
            >
              <Lock size={15} /> {closed ? 'Close again' : 'Close'}
            </Button>
            {confirmClose && (
              <div
                role="dialog"
                aria-label="Close the month"
                className="absolute right-0 top-full z-10 mt-1 w-80 rounded-lg border border-line bg-surface p-3 shadow-lg"
              >
                <p className="text-sm font-semibold text-ink">Close {monthLabel(month)}?</p>
                <p className="mt-1 text-xs text-ink-soft">
                  Simon processes the month&rsquo;s reports, then locks the month. If any report fails, the month stays
                  open and you&rsquo;ll see why.
                </p>
                <div className="mt-3 flex justify-end gap-2">
                  <Button variant="secondary" onClick={() => setConfirmClose(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      setConfirmClose(false);
                      start(close);
                    }}
                  >
                    Close {monthLabel(month)}
                  </Button>
                </div>
              </div>
            )}
          </div>
          {run.data && <RunBanner run={run.data} month={month} />}
        </div>

        {key && <MonthReports propertyKey={key} month={month} period={period} />}

        <h2 className="mt-8 text-sm font-semibold uppercase tracking-wider text-ink-faint">History</h2>
        <div className="mt-2 space-y-2">
          {periods.data?.length === 0 && (
            <p className="text-sm text-ink-faint">
              No months processed yet. Upload the month&rsquo;s reports, then Process.
            </p>
          )}
          {periods.data?.map((p) => (
            <MonthHistory
              key={p.id}
              period={p}
              onSelect={() => {
                setPropertyKey(p.propertyKey);
                setMonth(p.month);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PeriodStatusPill({ period }: { period?: Period }) {
  if (!period) return <Pill tone="faint">Not processed yet</Pill>;
  if (period.status === 'provisional') return <Pill tone="neutral">Provisional</Pill>;
  return (
    <span className="flex flex-col gap-0.5">
      <span>
        <Pill tone="sage">Closed</Pill>
      </span>
      <span className="text-xs text-ink-faint">
        {[period.closedByName, formatDateTime(period.closedAt)].filter(Boolean).join(' · ')}
      </span>
    </span>
  );
}

const RUN_LABEL: Record<PipelineRun['kind'], string> = { appfolio_ingest: 'Process', period_close: 'Close' };

const count = (o?: Record<string, unknown>) => Object.keys(o ?? {}).length;

function RunBanner({ run, month }: { run: PipelineRun; month: string }) {
  const what = `${RUN_LABEL[run.kind]} ${monthLabel(month)}`;
  if (isActive(run)) {
    return (
      <p role="status" className="mt-3 flex items-center gap-2 text-sm text-ink-soft">
        <RefreshCw size={14} className="animate-spin" /> {what}: {run.status}…
      </p>
    );
  }
  const ok = run.status === 'succeeded';
  return (
    <div
      role="status"
      className={`mt-3 rounded-lg p-3 text-sm ${ok ? 'bg-sage/10 text-ink' : 'bg-bronze-light/20 text-ink'}`}
    >
      <span className="font-semibold">
        {what}: {ok ? (run.kind === 'period_close' ? 'done, the month is closed' : 'done') : 'failed'}
      </span>
      <span className="ml-2 text-ink-soft">{summary(run)}</span>
      {run.error && <p className="mt-1 text-xs text-bronze">{run.error}</p>}
    </div>
  );
}

function summary(run: PipelineRun): string {
  const s = run.stats ?? {};
  const raised = s.conflicts?.raised.length ?? 0;
  const conflicts = raised ? ` · ${raised} new conflict${raised === 1 ? '' : 's'}` : '';
  return `${count(s.loaded)} loaded · ${count(s.skipped)} skipped · ${count(s.failed)} failed${conflicts}`;
}

type ReportState = { label: string; tone: 'sage' | 'neutral' | 'faint' | 'bronze'; detail?: string };

/**
 * What happened to (or will happen to) each report in the library, for this month.
 * runs: the month's finished runs, newest first (dry runs left out).
 */
export function reportState(doc: LibraryDocument, runs: PipelineRun[]): ReportState | null {
  const file = doc.originalFileName || doc.name;
  const last = runs[0];
  if (!last) return { label: 'Not processed yet', tone: 'faint' };
  if (last.finishedAt && new Date(doc.updatedAt) > new Date(last.finishedAt)) {
    return { label: 'Changed since last run', tone: 'neutral', detail: 'Process to load it' };
  }
  const s = last.stats ?? {};
  const loaded = s.loaded?.[file];
  if (loaded) return { label: `Loaded · revision ${loaded.revision}`, tone: 'sage', detail: `${loaded.rows} rows` };
  const failed = s.failed?.[file];
  if (failed) return { label: 'Failed', tone: 'bronze', detail: failed };
  const skipped = s.skipped?.[file];
  if (!skipped) return { label: 'Not processed yet', tone: 'faint' };
  if (/period ends .*, not /.test(skipped)) return null; // another month's report
  if (skipped.includes('already loaded')) {
    // the run that loaded it carries its revision and rows
    const when = runs.find((r) => r.stats?.loaded?.[file]);
    const was = when?.stats?.loaded?.[file];
    return was
      ? {
          label: `Loaded · revision ${was.revision}`,
          tone: 'sage',
          detail: `${was.rows} rows · unchanged since ${formatDate(when?.finishedAt)}`,
        }
      : { label: 'Loaded · unchanged', tone: 'sage' };
  }
  if (skipped.includes('no reader yet'))
    return { label: 'Stored · not read yet', tone: 'faint', detail: 'Searchable by Simon' };
  return { label: 'Skipped', tone: 'neutral', detail: skipped };
}

function MonthReports({ propertyKey, month, period }: { propertyKey: string; month: string; period?: Period }) {
  const docs = useDocuments('lease_reports', '', [propertyKey]);
  const runs = usePeriodRuns(period?.id);
  const finished = (runs.data ?? []).filter((r) => !isActive(r) && !r.params?.dryRun);
  const rows = (docs.data ?? [])
    .map((d) => ({ doc: d, state: reportState(d, finished) }))
    .filter((r): r is { doc: LibraryDocument; state: ReportState } => r.state !== null)
    .sort((a, b) => a.doc.name.localeCompare(b.doc.name));

  return (
    <>
      <h2 className="mt-8 text-sm font-semibold uppercase tracking-wider text-ink-faint">
        Reports for {monthLabel(month)}
      </h2>
      <div className="mt-2 overflow-hidden rounded-xl border border-line bg-surface">
        <table className="w-full text-sm">
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-ink-faint">
                  No reports in Documents for this property yet. Upload them under Lease reports.
                </td>
              </tr>
            )}
            {rows.map(({ doc, state }) => (
              <tr key={doc.id} className="border-b border-line-soft last:border-0">
                <td className="px-4 py-2 text-ink">{doc.name}</td>
                <td className="w-56 px-2 py-2">
                  <Pill tone={state.tone}>{state.label}</Pill>
                </td>
                <td className="px-2 py-2 text-xs text-ink-faint">{state.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function MonthHistory({ period, onSelect }: { period: Period; onSelect: () => void }) {
  const [open, setOpen] = useState(false);
  const runs = usePeriodRuns(open ? period.id : undefined);
  // dry runs (the script's --dry-run) change nothing: not history
  const history = runs.data?.filter((r) => !r.params?.dryRun);
  const Icon = open ? ChevronDown : ChevronRight;
  return (
    <div className="rounded-xl border border-line bg-surface">
      <div className="flex items-center gap-3 px-4 py-2.5">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <Icon size={14} className="text-ink-faint" />
          <span className="font-semibold text-ink">{monthLabel(period.month)}</span>
          <span className="text-sm text-ink-soft">{period.propertyName}</span>
          <Pill tone={period.status === 'closed' ? 'sage' : 'neutral'}>
            {period.status === 'closed' ? 'Closed' : 'Provisional'}
          </Pill>
          {period.status === 'closed' && (
            <span className="text-xs text-ink-faint">
              {[period.closedByName, formatDateTime(period.closedAt)].filter(Boolean).join(' · ')}
            </span>
          )}
        </button>
        <button type="button" onClick={onSelect} className="text-xs font-semibold text-ink-soft hover:text-ink">
          Select
        </button>
      </div>
      {open && (
        <table className="w-full border-t border-line text-sm">
          <tbody>
            {history?.length === 0 && (
              <tr>
                <td className="px-4 py-3 text-ink-faint">No runs yet.</td>
              </tr>
            )}
            {history?.map((r) => (
              <tr key={r.id} className="border-b border-line-soft align-top last:border-0">
                <td className="w-40 px-4 py-2 text-ink-soft">{formatDateTime(r.finishedAt ?? r.createdAt)}</td>
                <td className="w-32 px-2 py-2 text-ink-soft">{r.triggeredByName ?? ''}</td>
                <td className="w-28 px-2 py-2">
                  <Pill tone={r.kind === 'period_close' ? 'ink' : 'neutral'}>{RUN_LABEL[r.kind]}</Pill>
                </td>
                <td className="w-24 px-2 py-2">
                  <Pill tone={r.status === 'succeeded' ? 'sage' : r.status === 'failed' ? 'bronze' : 'faint'}>
                    {r.status}
                  </Pill>
                </td>
                <td className="px-2 py-2 text-xs text-ink-soft">
                  {summary(r)}
                  {r.params?.reason && <div className="text-ink">Note: {r.params.reason}</div>}
                  {r.error && <div className="text-bronze">{r.error}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
